// src/components/DoctorOnboarding.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../components/layout/index";
import { APP_ROUTES } from "../../constants/approutes";
import { toast } from "react-toastify";
import { DatePicker, TimePicker } from "antd";
import FullScreenLoader from "../../components/FullScreenLoader";
import dayjs from "dayjs";
import axios from "axios";
import { UserPlus } from "lucide-react";

const LANGUAGE_OPTIONS = [
  'English',
  'Hindi',
  'Bengali',
  'Telugu',
  'Marathi',
  'Tamil',
  'Gujarati',
  'Urdu',
  'Kannada',
  'Odia',
  'Punjabi',
  'Malayalam',
  'Assamese',
];

const MCI_RESULT_LABELS = {
  nmcDoctorId: "NMC Doctor ID",
  doctorName: "Doctor Name",
  parentName: "Parent Name",
  registrationNo: "Registration No",
  registrationDate: "Registration Date",
  yearInfo: "Year",
  stateMedicalCouncil: "State Medical Council",
  qualification: "Qualification",
  qualificationYear: "Qualification Year",
  university: "University",
  uprnNo: "UPRN No",
  address: "Address",
  dateOfBirth: "Date of Birth",
  removed: "Removed",
  restored: "Restored",
  remarks: "Remarks",
  blacklisted: "Blacklisted",
  blacklistCouncil: "Blacklist Council",
  blacklistSuspendedDate: "Blacklist Suspended Date",
  blacklistRestoredDate: "Blacklist Restored Date",
  blacklistRemarks: "Blacklist Remarks",
  source: "Source",
  checkedAt: "Checked At",
  errorMessage: "Error Message",
};

const MCI_RESULT_HIDDEN_FIELDS = ["removed", "restored", "blacklisted", "source", "checkedAt"];

// Specializations that don't require NMC/state medical council registration
const MCI_NOT_APPLICABLE_KEYWORDS = [
  "dietetic",
  "dietician",
  "dietitian",
  "nutrition",
  "physiotherap",
  "dentist",
  "dental",
];

// MCI is only skippable when EVERY selected specialization falls in the
// not-applicable list - if even one specialization (e.g. Cardiology) still
// requires NMC registration, verification stays required.
const isMciNotApplicableSpecialization = (specializations) =>
  Array.isArray(specializations) &&
  specializations.length > 0 &&
  specializations.every((spec) =>
    MCI_NOT_APPLICABLE_KEYWORDS.some((keyword) =>
      String(spec || "").toLowerCase().includes(keyword)
    )
  );

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const normalizeIfscCode = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s/g, "")
    .slice(0, 11);

const isValidIfscCode = (value) => IFSC_REGEX.test(normalizeIfscCode(value));

const getApiValidationMessage = (result) => {
  if (result?.data && typeof result.data === "object" && !Array.isArray(result.data)) {
    const fieldErrors = Object.values(result.data).filter(Boolean);
    if (fieldErrors.length > 0) return fieldErrors.join(", ");
  }
  return result?.message || result?.error || "";
};

function CurebayDoctorOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const parentCode = location.state?.parentCode;
  const doctorData = location.state?.doctorData || null;   // 👈 from CurebayDoctorList
  const isEdit = location.state?.isEdit || false;          // 👈 edit flag
  console.log(doctorData, "doctorData");
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const clinicId = localStorage.getItem("clinicId");
  console.log(clinicId, "clinicId");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isVerifyingPan, setIsVerifyingPan] = useState(false);
  const [panVerifyModal, setPanVerifyModal] = useState({ isOpen: false, message: "", status: "" });
  const [panVerified, setPanVerified] = useState("");
  const [isManualVerifyingPan, setIsManualVerifyingPan] = useState(false);
  const [isVerifyingBank, setIsVerifyingBank] = useState(false);
  const [bankVerifyModal, setBankVerifyModal] = useState({ isOpen: false, message: "", status: "" });
  const [bankVerified, setBankVerified] = useState("");
  const [isManualVerifyingBank, setIsManualVerifyingBank] = useState(false);
  const [isVerifyingMci, setIsVerifyingMci] = useState(false);
  const [mciVerifyModal, setMciVerifyModal] = useState({ isOpen: false, message: "", result: null, needsManualVerification: false });
  const [isManualVerifyingMci, setIsManualVerifyingMci] = useState(false);
  const [mciVerificationStatus, setMciVerificationStatus] = useState("");
  const [fieldErrors, setFieldErrors] = useState({}); // Track individual field errors
  const [isWeeksOpen, setIsWeeksOpen] = useState(false);
  const [isDaysOpen, setIsDaysOpen] = useState(false);
  const [isConsultationTypeOpen, setIsConsultationTypeOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  // Step-wise onboarding
  const [currentStep, setCurrentStep] = useState(1); // 1: Basic details, 2: Verify OTP, 3: Full form
  const [basicDetails, setBasicDetails] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    mobile: "",
    email: "",
  });
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [doctorUserId, setDoctorUserId] = useState("");

  const [formData, setFormData] = useState({
    doctorName: "",
    smcId: "",
    stateMedicalCouncil: "",
    licenseNumber: "",

    qualifications: "",
    specialization: [],
    doctorType: "",
    doctorSubType: "",
    experience: "",
    phone: "",
    email1: "",
    commissionPercentage: "",
    paymentPeriod: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    age: "",
    gender: "",
    languageSelection: [],
    isFamilyDoctor: "",
    fromDate: dayjs().format("YYYY-MM-DD"), // Initialize with current date
    toDate: dayjs().format("YYYY-MM-DD"),   // Initialize with current date
    timeSlots: [],
    dayOfWeek: "",
    bio: "",
    slotDuration: "",
    // Bank & account fields
    bankName: "",
    accountHolderName: "",
    ifscCode: "",
    accountNumber: "",
    bankLoc: "",
    // PAN / Aadhaar / Signature / Stamp / Profile
    panCardNumber: "",
    panPhoto: null,
    adhharCardNo: "",
    adharPhoto: null,
    signaturePhoto: null,
    stampPhoto: null,
    profilePhoto: null,
    cancelledChequePhoto: null,
    medicalRegistrationCertificatePhoto: null,
  });

  // New state for specialization search and options
  const [specializationSearch, setSpecializationSearch] = useState("");
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [loadingSpecializations, setLoadingSpecializations] = useState(false);
  const [showSpecializationDropdown, setShowSpecializationDropdown] =
    useState(false);

  // Fetch specializations from API
  const fetchSpecializations = async () => {
    try {
      setLoadingSpecializations(true);
      const response = await fetch(
            `${process.env.REACT_APP_API_URL_PATIENTS}Speciality/list?status=1`,
        { method: "GET" }
      );

      if (response.ok) {
        const data = await response.json();
        setSpecializationOptions(
          (Array.isArray(data) ? data : [])
            .map((item) => item.speciality)
            .filter(Boolean)
        );
      } else {
        console.error("Failed to fetch specializations");
        setSpecializationOptions([]);
      }
    } catch (error) {
      console.error("Error fetching specializations:", error);
      setSpecializationOptions([]);
    } finally {
      setLoadingSpecializations(false);
    }
  };

  // State for state medical council search and options
  const [medicalCouncilSearch, setMedicalCouncilSearch] = useState("");
  const [medicalCouncilOptions, setMedicalCouncilOptions] = useState([]);
  const [loadingMedicalCouncils, setLoadingMedicalCouncils] = useState(false);
  const [showMedicalCouncilDropdown, setShowMedicalCouncilDropdown] =
    useState(false);

  // Fetch state medical councils from API
  const fetchMedicalCouncils = async () => {
    try {
      setLoadingMedicalCouncils(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/v1/nmc-verification/councils`,
        { method: "GET" }
      );

      if (response.ok) {
        const data = await response.json();
        setMedicalCouncilOptions(Array.isArray(data) ? data : []);
      } else {
        console.error("Failed to fetch medical councils");
        setMedicalCouncilOptions([]);
      }
    } catch (error) {
      console.error("Error fetching medical councils:", error);
      setMedicalCouncilOptions([]);
    } finally {
      setLoadingMedicalCouncils(false);
    }
  };

  // Handle state medical council selection (single select)
  const handleMedicalCouncilSelect = (council) => {
    setFormData((prev) => ({
      ...prev,
      smcId: council.smcId,
      stateMedicalCouncil: council.name,
    }));
    setMedicalCouncilSearch(council.name);
    setShowMedicalCouncilDropdown(false);
    setMciVerificationStatus(""); // council changed, previous verification no longer applies
  };

  // Some specializations (dietitians, physiotherapists, dentists, etc.) and the
  // "Take Care" doctor type don't require NMC/state medical council registration.
  const isMciNotApplicable =
    isMciNotApplicableSpecialization(formData.specialization) ||
    formData.doctorType === "TAKECARE" || formData.doctorType === "TOTALCARE";

  useEffect(() => {
    const notApplicable = isMciNotApplicable;
    setMciVerificationStatus((prev) => {
      if (notApplicable) return "NOT_APPLICABLE";
      return prev === "NOT_APPLICABLE" ? "" : prev;
    });
    if (notApplicable) {
      setFieldErrors((prev) => (prev.licenseNumber ? { ...prev, licenseNumber: null } : prev));
      setFormData((prev) => ({
        ...prev,
        licenseNumber: "",
        smcId: "",
        stateMedicalCouncil: "",
      }));
      setMedicalCouncilSearch("");
    }
  }, [isMciNotApplicable]);

  // Load specializations and medical councils on component mount
  useEffect(() => {
    fetchSpecializations();
    fetchMedicalCouncils();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest(".specialization-dropdown-container")) {
        setShowSpecializationDropdown(false);
      }
      if (!target.closest(".medical-council-dropdown-container")) {
        setShowMedicalCouncilDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close weeks and days dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest(".weeks-dropdown-container")) {
        setIsWeeksOpen(false);
      }
      if (!target.closest(".days-dropdown-container")) {
        setIsDaysOpen(false);
      }
      if (!target.closest(".consultation-type-dropdown-container")) {
        setIsConsultationTypeOpen(false);
      }
      if (!target.closest(".language-dropdown-container")) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle specialization selection
  const handleSpecializationSelect = (specialization) => {
    if (!formData.specialization.includes(specialization)) {
      setFormData((prev) => ({
        ...prev,
        specialization: [...prev.specialization, specialization],
      }));
      // Clear error when field is filled
      setFieldErrors(prev => ({ ...prev, specialization: null }));
    }
    setSpecializationSearch("");
    setShowSpecializationDropdown(false);
  };

  // Handle specialization removal
  const handleSpecializationRemove = (specializationToRemove) => {
    const updatedSpecializations = formData.specialization.filter(
      (spec) => spec !== specializationToRemove
    );
    setFormData((prev) => ({
      ...prev,
      specialization: updatedSpecializations,
    }));
    // Add error if no specializations left
    if (updatedSpecializations.length === 0) {
      setFieldErrors(prev => ({ ...prev, specialization: "At least one specialization is required" }));
    }
  };

  // New state for consultation fees
  const [consultationFeeEntry, setConsultationFeeEntry] = useState({
    consultationtype: "",
    consultationFee: "",
  });
  const [consultationFees, setConsultationFees] = useState([]);

  const [currentSlot, setCurrentSlot] = useState({
    dayOfWeek: [],
    startTime: "",
    endTime: "",
    weekNumbers: [1, 2, 3, 4, 5],
    consultationType: [],
  });
  const consultationTypeOptions = [
    { label: "Video", value: "V" },
    { label: "In-Person", value: "I" },
    { label: "Camp", value: "C" },
    { label: "Home Visit", value: "H" },
  ];
  const getConsultationTypeLabel = (value) =>
    consultationTypeOptions.find((option) => option.value === value)?.label ||
    value;
  const serializeTimeSlots = (timeSlots) =>
    timeSlots.map((slot) => ({
      ...slot,
      consultationType: Array.isArray(slot.consultationType)
        ? slot.consultationType.join(",")
        : slot.consultationType,
    }));

  // Auto-fill city/state from pincode (similar to AddAddress)
  const fetchAddressByPincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");

      const response = await axios.get(
        `${process.env.REACT_APP_NEWBASEURL}getAllAddressDetails/list?pincode=${pincode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );

      const list = response?.data?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        const addressData = list[0];

        setFormData((prev) => ({
          ...prev,
          state: addressData.state || prev.state || "",
          city: addressData.area || prev.city || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
      toast.error("Failed to fetch address details");
    }
  };

  // Pre-fill form when coming from list (edit/onboard existing doctor)
  useEffect(() => {
    if (!doctorData) return;

    const nextLicense =
      doctorData.licenseNumber ||
      doctorData.medicalLicenseNumber ||
      "";

    let nextSpecialization = [];
    if (Array.isArray(doctorData.specialization)) {
      nextSpecialization = doctorData.specialization.filter(Boolean);
    } else if (typeof doctorData.specialization === "string" && doctorData.specialization.trim()) {
      nextSpecialization = doctorData.specialization
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (doctorData.speciality) {
      nextSpecialization = [doctorData.speciality].filter(Boolean);
    }

    setFormData((prev) => ({
      ...prev,
      doctorName: doctorData.doctorName || prev.doctorName,
      phone: doctorData.phone || prev.phone,
      email1: doctorData.email1 || doctorData.email || prev.email1,
      ...(nextLicense ? { licenseNumber: nextLicense } : {}),
      ...(nextSpecialization.length > 0 ? { specialization: nextSpecialization } : {}),
    }));

    setFieldErrors((prev) => ({
      ...prev,
      ...(nextLicense ? { licenseNumber: null } : {}),
      ...(nextSpecialization.length > 0 ? { specialization: null } : {}),
    }));

    setBasicDetails((prev) => ({
      ...prev,
      firstName: doctorData.doctorName || prev.firstName,
      mobile: doctorData.phone || prev.mobile,
      email: doctorData.email || prev.email,
    }));
  }, [doctorData]);

  // Step 1: basic details handlers
  const handleBasicDetailsChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // For mobile number, allow only digits and max length 10
    if (name === "mobile") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setBasicDetails((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleStep1Next = async () => {
    const { firstName, lastName, mobile, email } = basicDetails;

    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First name and last name are required.");
      return;
    }
    // At least one of mobile or email is required
    if (!mobile.trim() && !email.trim()) {
      toast.error("Please enter either Mobile Number or Email.");
      return;
    }
    // If mobile is provided, validate its length
    if (mobile.trim() && mobile.trim().length !== 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }

    setIsSendingOtp(true);
    setOtp("");

    try {
      const response = await fetch(`${API_URL}auth/onboard/register`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          middleName: basicDetails.middleName?.trim() || "",
          lastName: lastName.trim(),
          mobile: mobile.trim(),
          email: email.trim(),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result?.success === true) {
        setRequestId(result?.requestId ?? "");
        setDoctorUserId(result?.doctorUserId ?? "");
        setOtpSent(true);
        toast.success(result?.message || "OTP sent successfully.");
        setCurrentStep(2);
      } else {
        const msg = result?.message || "Failed to register doctor / send OTP.";
        toast.error(msg);
      }
    } catch (error) {
      console.error("Error calling onboarding/register:", error);
      toast.error("Something went wrong while registering doctor.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 2: verify OTP and move to full form
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error("Enter a valid OTP.");
      return;
    }
    if (!requestId) {
      toast.error("Session expired. Please go back and register again.");
      return;
    }

    const { mobile, email, firstName, middleName, lastName } = basicDetails;

    setIsVerifyingOtp(true);
    try {
      const response = await fetch(`${API_URL}auth/onboard/verify-otp`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          requestId,
          otp,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        const msg = data?.message || "Failed to verify OTP.";
        toast.error(msg);
        return;
      }


      const fullName = [firstName, middleName, lastName]
        .map((p) => p && p.trim())
        .filter(Boolean)
        .join(" ");

      // Prefill main form with basic details
      setFormData((prev) => ({
        ...prev,
        doctorName: fullName,
        phone: mobile,
        email1: email,
      }));

      toast.success("OTP verified successfully.");
      setCurrentStep(3);
    } catch (error) {
      console.error("Error verifying OTP for doctor onboarding:", error);
      toast.error("Something went wrong while verifying OTP.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Validate individual field and update errors
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'doctorName':
        if (!value.trim()) error = "Doctor name is required";
        break;
      case 'licenseNumber':
        if (!isMciNotApplicable && !value.trim()) {
          error = "Medical license number is required";
        }
        break;
      case 'qualifications':
        if (!value.trim()) error = "Qualifications are required";
        break;
      case 'specialization':
        if (!Array.isArray(value) || value.length === 0) error = "At least one specialization is required";
        break;
      case 'doctorType':
        if (!value?.trim?.()) error = "Doctor type is required";
        break;
      case 'doctorSubType':
        if ((formData.doctorType === "EMPANELLED" || formData.doctorType === "INVENTORY" || formData.doctorType === "FREELANCER" ) && !value?.trim?.()) {
          error = "Doctor sub type is required";
        }
        break;
      case 'isFamilyDoctor':
        if (value !== "true" && value !== "false") error = "Please select whether this is a family doctor";
        break;
      case 'experience':
        if (!value.trim()) error = "Experience is required";
        break;
      case 'phone':
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (value.trim().length !== 10) {
          error = "Phone number must be exactly 10 digits";
        }
        break;
      case 'email1':
        if (!value.trim()) error = "Email is required";
        break;
      case 'fromDate':
        if (!value) error = "From date is required";
        break;
      case 'toDate':
        if (!value) error = "To date is required";
        break;
      case 'timeSlots':
        if (!Array.isArray(value) || value.length === 0) error = "At least one time slot is required";
        break;
      case 'bio':
        if (!value.trim()) error = "Doctor bio is required";
        break;
      case 'bankName':
        // Bank name is now optional
        break;
      case 'bankLoc':
        // Branch location is now optional
        break;
      case 'accountHolderName':
        // Account holder name is now optional
        break;
      case 'ifscCode': {
        // IFSC code is now optional, but must be valid if provided
        const normalizedIfsc = normalizeIfscCode(value);
        if (normalizedIfsc && !isValidIfscCode(normalizedIfsc)) {
          error = "Enter a valid IFSC code (e.g. SBIN0001234)";
        }
        break;
      }
      case 'accountNumber':
        // Account number is now optional
        break;
      case 'panCardNumber':
        if (!value.trim()) {
          error = "PAN card number is required";
        } else if (value.trim().length !== 10) {
          error = "PAN card number must be exactly 10 characters";
        }
        break;
      case 'panPhoto':
        if (!value) {
          error = "PAN photo is required";
        }
        break;
      case 'adhharCardNo':
        // Aadhaar number is now optional
        if (value.trim() && value.trim().length !== 12) {
          error = "Aadhaar number must be exactly 12 digits";
        }
        break;
      case 'adharPhoto':
        // Aadhaar photo is now optional
        break;
      case 'signaturePhoto':
        // Signature photo is now optional
        break;
      case 'stampPhoto':
        // Stamp photo is now optional
        break;
      case 'profilePhoto':
        // Profile photo is now optional
        break;
      case 'consultationFees':
        if (!Array.isArray(value) || value.length === 0) error = "At least one consultation fee is required";
        break;
      case 'slotDuration':
        if (!value.trim()) {
          error = "Slot duration is required";
        } else if (!/^\d+$/.test(value)) {
          error = "Slot duration must be a number";
        } else if (parseInt(value, 10) <= 0) {
          error = "Slot duration must be greater than 0";
        }
        break;
    }

    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    let newValue = value;

    // Editing a verified field invalidates its previous verification
    if (name === "licenseNumber") {
      setMciVerificationStatus("");
    } else if (name === "panCardNumber") {
      setPanVerified("");
    } else if (name === "accountHolderName" || name === "ifscCode" || name === "accountNumber") {
      setBankVerified("");
    }

    if (type === "file") {
      newValue = files[0] || null;
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      validateField(name, newValue);
      return;
    }

    if (name === "doctorType") {
      const requiresSubType = value === "EMPANELLED" || value === "INVENTORY" || value === "FREELANCER";
      setFormData((prev) => ({
        ...prev,
        doctorType: value,
        doctorSubType: requiresSubType ? prev.doctorSubType : "",
      }));
      validateField("doctorType", value);
      if (!requiresSubType) {
        setFieldErrors((prev) => ({ ...prev, doctorSubType: null }));
      }
      return;
    }

    // Handle date fields - keep as string format
    if (name === "fromDate" || name === "toDate") {
      newValue = value; // Already in YYYY-MM-DD format from DatePicker
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      validateField(name, newValue);
      return;
    }

    // Handle boolean-select fields
    if (name === "isFamilyDoctor") {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      validateField(name, newValue);
      return;
    }

    // Field-specific sanitization/validation
    if (name === "phone") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "adhharCardNo") {
      newValue = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "accountHolderName") {
      newValue = value.replace(/[^a-zA-Z ]/g, "");
    } else if (name === "accountNumber") {
      newValue = value.replace(/\D/g, "");
    } else if (name === "commissionPercentage") {
      newValue = value.replace(/[^0-9.]/g, "");
    } else if (name === "pinCode") {
      newValue = value.replace(/\D/g, "").slice(0, 6);
    } else if (name === "age") {
      newValue = value.replace(/\D/g, "").slice(0, 3);
    } else if (name === "doctorName") {
      newValue = value.replace(/[^a-zA-Z ]/g, "");
    } else if (name === "bankName") {
      newValue = value.replace(/[^a-zA-Z ]/g, "");
    } else if (name === "ifscCode") {
      newValue = normalizeIfscCode(value);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    validateField(name, newValue);
  };

  const isFormValid = () => {
    return (
      formData.doctorName.trim() &&
      (isMciNotApplicable || formData.licenseNumber.trim()) &&
      formData.qualifications.trim() &&
      formData.specialization.length > 0 &&
      formData.doctorType.trim() &&
      ((formData.doctorType !== "EMPANELLED" && formData.doctorType !== "INVENTORY" && formData.doctorType !== "FREELANCER")  || formData.doctorSubType.trim()) &&
      formData.experience.trim() &&
      formData.phone.trim().length === 10 &&
      formData.email1.trim() &&
      (formData.isFamilyDoctor === "true" || formData.isFamilyDoctor === "false") &&
      formData.fromDate &&
      formData.toDate &&
      formData.timeSlots.length > 0 &&
      formData.bio.trim() &&
      consultationFees.length > 0 &&
      formData.panCardNumber.trim() &&
      formData.panPhoto
      //–– remaining bank fields and document fields are still optional
    );
  };

  // Validate all fields and show errors
  const validateAllFields = () => {
    const errors = {};

    // Validate all required fields (documents are now optional)
    const fieldsToValidate = [
      'doctorName', 'licenseNumber', 'qualifications', 'specialization', 'doctorType',
      'experience', 'phone', 'email1', 'isFamilyDoctor', 'fromDate', 'toDate', 'timeSlots',
      'bio', 'slotDuration', 'panCardNumber', 'panPhoto',
    ];

    if (formData.doctorType === "EMPANELLED" || formData.doctorType === "INVENTORY" || formData.doctorType === "FREELANCER")  {
      fieldsToValidate.push('doctorSubType');
    }

    // Validate IFSC format only if provided, since bank details are now optional
    if (formData.ifscCode.trim()) {
      fieldsToValidate.push('ifscCode');
    }
    if (formData.adhharCardNo.trim()) {
      fieldsToValidate.push('adhharCardNo');
    }

    fieldsToValidate.forEach(fieldName => {
      const fieldValue = formData[fieldName];
      const error = validateField(fieldName, fieldValue);
      if (error) {
        errors[fieldName] = error;
      }
    });

    // Validate consultation fees separately
    if (consultationFees.length === 0) {
      errors.consultationFees = "At least one consultation fee is required";
      setFieldErrors(prev => ({ ...prev, consultationFees: "At least one consultation fee is required" }));
    }

    return Object.keys(errors).length === 0;
  };

  const handleVerifyPan = async () => {
    if (!formData.panCardNumber.trim()) {
      toast.error("Please enter PAN Card Number before verifying.");
      return;
    }
    if (!formData.email1.trim()) {
      toast.error("Please enter Email Id before verifying PAN.");
      return;
    }

    setIsVerifyingPan(true);
    try {
      const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_DOCTOR_URL}doctor/pan/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email: formData.email1,
          panNumber: formData.panCardNumber,
          panHolderName: formData.doctorName,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setPanVerifyModal({
          isOpen: true,
          message: data?.data?.message || data?.message || "PAN verification failed. Please check the PAN details and try again.",
        });
        return;
      }

      const panStatus = data?.data?.status;
      setPanVerified(panStatus || "");

      if (panStatus === "INCONCLUSIVE" || panStatus === "NOT_VERIFIED") {
        let modalMessage = data?.data?.message || "PAN verification could not be completed.";
        if (panStatus === "NOT_VERIFIED") {
          modalMessage = modalMessage.replace(
            /Please check and try again\.?/i,
            "Please verify manually."
          );
        }
        setPanVerifyModal({
          isOpen: true,
          message: modalMessage,
          status: panStatus,
        });
        return;
      }

      toast.success("PAN verified successfully.");
    } catch (error) {
      console.error("Error verifying PAN:", error);
      setPanVerifyModal({
        isOpen: true,
        message: "Something went wrong while verifying PAN.",
        status: "",
      });
    } finally {
      setIsVerifyingPan(false);
    }
  };

  const handleManualPanVerification = async (statusValue) => {
    setIsManualVerifyingPan(true);
    try {
      const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_DOCTOR_URL}doctor/verification/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email: formData.email1,
          type: "PAN",
          status: statusValue,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "Failed to update PAN verification status.");
        return;
      }

      const approved = Boolean(data?.data?.status);
      if (approved) {
        setPanVerified("VERIFIED");
        toast.success("PAN marked as verified.");
      } else {
        setPanVerified("NOT_VERIFIED");
        toast.error("PAN verification declined.");
      }
      setPanVerifyModal({ isOpen: false, message: "", status: "" });
    } catch (error) {
      console.error("Error updating manual PAN verification:", error);
      toast.error("Something went wrong while updating PAN verification.");
    } finally {
      setIsManualVerifyingPan(false);
    }
  };

  const isPanVerificationSatisfied = () => {
    if (!formData.panCardNumber.trim()) return true; // PAN not entered, nothing to verify
    return panVerified === "VERIFIED";
  };

  const isBankVerificationSatisfied = () => {
    const hasBankInfo =
      formData.accountHolderName.trim() ||
      formData.ifscCode.trim() ||
      formData.accountNumber.trim();
    if (!hasBankInfo) return true; // Bank details not entered, nothing to verify
    return bankVerified === "VERIFIED";
  };

  const handleSubmitClick = () => {
    if (!isPanVerificationSatisfied() || !isBankVerificationSatisfied()) {
      toast.error("Please verify PAN and Bank details before submitting.");
      return;
    }
    handleNext();
  };

  const handleVerifyBank = async () => {
    const { accountHolderName, ifscCode, accountNumber, email1 } = formData;

    if (!accountHolderName.trim() || !ifscCode.trim() || !accountNumber.trim()) {
      toast.error("Please fill Account Holder Name, IFSC Code and Account Number before verifying.");
      return;
    }
    if (!email1.trim()) {
      toast.error("Please enter Email Id before verifying bank details.");
      return;
    }

    setIsVerifyingBank(true);
    try {
      const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_DOCTOR_URL}doctor/bank/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email: email1,
          accountNumber,
          ifscCode: normalizeIfscCode(ifscCode),
          accountHolderName,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setBankVerifyModal({
          isOpen: true,
          message: data?.data?.message || data?.message || "Bank verification failed. Please check the bank details and try again.",
        });
        return;
      }

      const bankStatus = data?.data?.status;
      setBankVerified(bankStatus || "");

      if (bankStatus === "NOT_VERIFIED") {
        const rawMessage = data?.data?.message || "Bank verification could not be completed.";
        const isNameMismatch = /doesn't match our records/i.test(rawMessage);
        const modalMessage = isNameMismatch
          ? rawMessage.replace(/Please check and try again\.?/i, "Please verify manually.")
          : rawMessage;

        setBankVerifyModal({
          isOpen: true,
          message: modalMessage,
          status: isNameMismatch ? bankStatus : "",
        });
        return;
      }

      if (bankStatus === "INCONCLUSIVE") {
        setBankVerifyModal({
          isOpen: true,
          message: data?.data?.message || "Bank verification could not be completed.",
          status: "",
        });
        return;
      }

      toast.success("Bank details verified successfully.");
    } catch (error) {
      console.error("Error verifying bank details:", error);
      setBankVerifyModal({
        isOpen: true,
        message: "Something went wrong while verifying bank details.",
        status: "",
      });
    } finally {
      setIsVerifyingBank(false);
    }
  };

  const handleManualBankVerification = async (statusValue) => {
    setIsManualVerifyingBank(true);
    try {
      const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_DOCTOR_URL}doctor/verification/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email: formData.email1,
          type: "BANK",
          status: statusValue,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "Failed to update bank verification status.");
        return;
      }

      const approved = Boolean(data?.data?.status);
      if (approved) {
        setBankVerified("VERIFIED");
        toast.success("Bank details marked as verified.");
      } else {
        setBankVerified("NOT_VERIFIED");
        toast.error("Bank verification declined.");
      }
      setBankVerifyModal({ isOpen: false, message: "", status: "" });
    } catch (error) {
      console.error("Error updating manual bank verification:", error);
      toast.error("Something went wrong while updating bank verification.");
    } finally {
      setIsManualVerifyingBank(false);
    }
  };

  const handleVerifyMci = async () => {
    if (!formData.licenseNumber.trim()) {
      toast.error("Please enter Medical License Number before verifying.");
      return;
    }
    if (!formData.stateMedicalCouncil.trim()) {
      toast.error("Please select State Medical Council before verifying.");
      return;
    }

    setIsVerifyingMci(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(
        "https://smartclinic-stage.curebay.in/smartclinic/api/v1/nmc-verification/verify",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            registrationNumber: formData.licenseNumber,
            medicalCouncil: formData.stateMedicalCouncil,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      const mciStatus = data?.status;
      const verified = mciStatus === "VERIFIED";
      setMciVerificationStatus(mciStatus || "");

      if (!verified) {
        setMciVerifyModal({
          isOpen: true,
          message: data?.message || "Medical license verification failed.",
          result: null,
          needsManualVerification: true,
        });
        return;
      }

      setMciVerifyModal({
        isOpen: true,
        message: data?.message || "Medical license verified successfully.",
        result: data?.result || null,
        needsManualVerification: false,
      });
    } catch (error) {
      console.error("Error verifying medical license:", error);
      setMciVerificationStatus("");
      setMciVerifyModal({
        isOpen: true,
        message: "Something went wrong while verifying the medical license.",
        result: null,
        needsManualVerification: true,
      });
    } finally {
      setIsVerifyingMci(false);
    }
  };

  const handleManualMciVerification = async () => {
    setIsManualVerifyingMci(true);
    try {
      const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_DOCTOR_URL}doctor/verification/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email: formData.email1,
          type: "MCI",
          status: "VERIFIED",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "Failed to update medical license verification status.");
        return;
      }

      const approved = Boolean(data?.data?.status);
      setMciVerificationStatus(approved ? "VERIFIED" : "NOT_VERIFIED");
      if (approved) {
        toast.success("Medical license marked as verified.");
      } else {
        toast.error("Medical license verification declined.");
      }
      setMciVerifyModal({ isOpen: false, message: "", result: null, needsManualVerification: false });
    } catch (error) {
      console.error("Error updating manual MCI verification:", error);
      toast.error("Something went wrong while updating medical license verification.");
    } finally {
      setIsManualVerifyingMci(false);
    }
  };

  const handleNext = async () => {
    const tenantId =
      formData.doctorType === "FREELANCER"
        ? "DoctorFreelancer"
        : localStorage.getItem("tenantId");
    const clinicId = localStorage.getItem("clinicId");

    if (!validateAllFields()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    setIsLoggingIn(true);

    const qualificationArray = Array.isArray(formData.qualifications)
      ? formData.qualifications
      : formData.qualifications
        .split(",")
        .map((q) => q.trim())
        .filter((q) => q.length > 0);

    const normalizedIfsc = normalizeIfscCode(formData.ifscCode);

    const doctorPayload = {
      fullName: formData.doctorName,
      ...(formData.licenseNumber.trim() && { medicalLicenseNumber: formData.licenseNumber }),

      qualifications: qualificationArray, // now definitely an array
      specialization: formData.specialization, // now an array
      doctorType: formData.doctorType,
      doctorSubType: formData.doctorSubType,
      yearsOfExperience: parseInt(formData.experience, 10),
      bio: formData.bio,
      slotDuration: formData.slotDuration,
      phoneNumber: formData.phone,
      email: formData.email1,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      pinCode: formData.pinCode,
      age: formData.age ? parseInt(formData.age, 10) : null,
      gender: formData.gender,
      languageSelection: formData.languageSelection,
      isFamilyDoctor: formData.isFamilyDoctor === "true",
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      timeSlots: serializeTimeSlots(formData.timeSlots),
      doctorFee: consultationFees,
      commissionPercentage: formData.commissionPercentage
        ? parseFloat(formData.commissionPercentage)
        : null,
      paymentPeriod: formData.paymentPeriod,
      clinicId: tenantId,
      bankName: formData.bankName,
      accountHolderName: formData.accountHolderName,
      // ifsc: normalizedIfsc,
      ifscCode: normalizedIfsc,
      accountNumber: formData.accountNumber,
      panCardNumber: formData.panCardNumber,
      panVerified: panVerified === "VERIFIED",
      bankVerified: bankVerified === "VERIFIED",
      mciVerificationStatus: mciVerificationStatus,
      adharCardNo: formData.adhharCardNo,
      fcmToken: "",
      bioMetricID: "",
      bankLoc: formData.bankLoc,
      tenantId: tenantId,
      doctorStatus: "ACTIVE",
      newDoctorRequestId: doctorData ? doctorData.newDoctorRequestId : null,
    };
 console.log("Doctor Onboarding Payload:", doctorPayload);
    const payload = new FormData();
    payload.append("doctor", JSON.stringify(doctorPayload));
    if (formData.panPhoto) {
      payload.append("panPhoto", formData.panPhoto);
    }
    if (formData.adharPhoto) {
      payload.append("adharPhoto", formData.adharPhoto);
    }
    if (formData.signaturePhoto) {
      payload.append("signaturePhoto", formData.signaturePhoto);
    }
    if (formData.stampPhoto) {
      payload.append("stampPhoto", formData.stampPhoto);
    }
    if (formData.profilePhoto) {
      payload.append("profilePhoto", formData.profilePhoto);
    }
    if (formData.cancelledChequePhoto) {
      payload.append("cancelledChequePhoto", formData.cancelledChequePhoto);
    }
    if (formData.medicalRegistrationCertificatePhoto) {
      payload.append(
        "medicalRegistrationCertificatePhoto",
        formData.medicalRegistrationCertificatePhoto
      );
    }

    console.log("Doctor Onboarding Payload:", payload);
    const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_DOCTOR_URL}doctor/complete-onboard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // tenantId: tenantId,
        },
        body: payload,
      });

      const result = await res.json();
      //  setIsLoggingIn(false);
      console.log(result.error, "dcfvgbhnjk")

      if (result.status === "SUCCESS") {
        // toast.success("Doctor onboarded successfully!");
        handleSubmitTenant(result.doctorCode, result.doctorProfileId);
      } else {
        setIsLoggingIn(false);
        const msg = getApiValidationMessage(result) || "Onboarding failed.";
        toast.error(msg);
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error("Something went wrong while onboarding.");
      setIsLoggingIn(false);
    }

  };

  const handleSubmitTenant = async (doctorCode, doctorProfileId) => {
    const tenantId =
      formData.doctorType === "FREELANCER"
        ? "DoctorFreelancer"
        : localStorage.getItem("tenantId");
    const clinicId = localStorage.getItem("clinicId");
    setIsLoggingIn(true);

    const qualificationArray = Array.isArray(formData.qualifications)
      ? formData.qualifications
      : formData.qualifications
        .split(",")
        .map((q) => q.trim())
        .filter((q) => q.length > 0);

    // const doctorPayload = {
    //   fullName: formData.doctorName,
    //   medicalLicenseNumber: formData.licenseNumber,
    //   qualifications: qualificationArray, // now definitely an array
    //   specialization: formData.specialization, // now an array
    //   yearsOfExperience: parseInt(formData.experience, 10),
    //   bio: formData.bio,
    //   slotDuration: formData.slotDuration,
    //   phoneNumber: formData.phone,
    //   email: formData.email1,
    //   fromDate: formData.fromDate,
    //   toDate: formData.toDate,
    //   timeSlots: formData.timeSlots,
    //   doctorFee: consultationFees,
    //   clinicId: clinicId,
    //   bankName: formData.bankName,
    //   accountHolderName: formData.accountHolderName,
    //   ifscCode: formData.ifscCode,
    //   accountNumber: formData.accountNumber,
    //   panCardNumber: formData.panCardNumber,
    //   adharCardNo: formData.adhharCardNo,
    //   fcmToken: "",
    //   bioMetricID: "",
    //   bankLoc: formData.bankLoc,
    //   tenantId:tenantId,
    //   doctorCode:doctorCode
    // };
    const doctorPayload = {
      doctorCode: doctorCode,
      id: doctorProfileId,
      fullName: formData.doctorName,
      ...(formData.licenseNumber.trim() && { medicalLicenseNumber: formData.licenseNumber }),

      qualifications: qualificationArray, // now definitely an array
      specialization: formData.specialization, // now an array
      doctorType: formData.doctorType,
      doctorSubType: formData.doctorSubType,
      yearsOfExperience: parseInt(formData.experience, 10),
      bio: formData.bio,
      slotDuration: formData.slotDuration,
      phoneNumber: formData.phone,
      email: formData.email1,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      pinCode: formData.pinCode,
      age: formData.age ? parseInt(formData.age, 10) : null,
      gender: formData.gender,
      languageSelection: formData.languageSelection,
      isFamilyDoctor: formData.isFamilyDoctor === "true",
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      timeSlots: serializeTimeSlots(formData.timeSlots),
      doctorFee: consultationFees,
      commissionPercentage: formData.commissionPercentage
        ? parseFloat(formData.commissionPercentage)
        : null,
      paymentPeriod: formData.paymentPeriod,
      clinicId: tenantId,
      bankName: formData.bankName,
      accountHolderName: formData.accountHolderName,
      // ifsc: normalizeIfscCode(formData.ifscCode),
      ifscCode: normalizeIfscCode(formData.ifscCode),
      accountNumber: formData.accountNumber,
      panCardNumber: formData.panCardNumber,
      panVerified: panVerified === "VERIFIED",
      bankVerified: bankVerified === "VERIFIED",
      mciVerificationStatus: mciVerificationStatus,
      adharCardNo: formData.adhharCardNo,
      fcmToken: "",
      bioMetricID: "",
      bankLoc: formData.bankLoc,
      tenantId: tenantId,
      doctorStatus: "ACTIVE",
      newDoctorRequestId: doctorData?.newDoctorRequestId,
    };

    const payload = new FormData();
    payload.append("doctor", JSON.stringify(doctorPayload));
    if (formData.panPhoto) {
      payload.append("panPhoto", formData.panPhoto);
    }
    if (formData.adharPhoto) {
      payload.append("adharPhoto", formData.adharPhoto);
    }
    if (formData.signaturePhoto) {
      payload.append("signaturePhoto", formData.signaturePhoto);
    }
    if (formData.stampPhoto) {
      payload.append("stampPhoto", formData.stampPhoto);
    }
    if (formData.profilePhoto) {
      payload.append("profilePhoto", formData.profilePhoto);
    }
    if (formData.cancelledChequePhoto) {
      payload.append("cancelledChequePhoto", formData.cancelledChequePhoto);
    }
    if (formData.medicalRegistrationCertificatePhoto) {
      payload.append(
        "medicalRegistrationCertificatePhoto",
        formData.medicalRegistrationCertificatePhoto
      );
    }

    const token = localStorage.getItem("auth_token");
    const API_DOCTOR_URL = process.env.REACT_APP_API_URL;
    try {
      const res = await fetch(`${API_DOCTOR_URL}doctor/complete-onboard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
        body: payload,
      });

      const result = await res.json();
      setIsLoggingIn(false);
      if (res.ok) {
        toast.success("Doctor onboarded successfully!");
        navigate("/CurebayDoctorList");
      } else {
        const msg = getApiValidationMessage(result) || "Onboarding failed.";
        toast.error(msg);
      }


    } catch (error) {
      console.error("API error:", error);
      setIsLoggingIn(false);

    }
  };

  // Step 1 UI: collect basic details
  // if (currentStep === 1) {
  //   return (
  //     <>
  //       {isLoggingIn && <FullScreenLoader message="" />}

  //       <DashboardLayout>
  //         <div className="min-h-screen bg-white p-4 md:pt-10">
  //           <h2 className="text-lg font-semibold text-[#004276] md:text-xl">
  //             Doctor Onboard
  //           </h2>

  //           <div className="mt-8 space-y-8">
  //             <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
  //               <h3 className="mb-4 text-lg font-semibold text-[#004276]">
  //                 Step 1 of 3 - Basic Details
  //               </h3>
  //               <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
  //                 <InputField
  //                   label="First Name"
  //                   name="firstName"
  //                   value={basicDetails.firstName}
  //                   onChange={handleBasicDetailsChange}
  //                 />
  //                 <InputField
  //                   label="Middle Name"
  //                   name="middleName"
  //                   value={basicDetails.middleName}
  //                   onChange={handleBasicDetailsChange}
  //                   showRequired={false}
  //                 />
  //                 <InputField
  //                   label="Last Name"
  //                   name="lastName"
  //                   value={basicDetails.lastName}
  //                   onChange={handleBasicDetailsChange}
  //                 />
  //                 <InputField
  //                   label="Mobile Number"
  //                   name="mobile"
  //                   type="tel"
  //                   maxLength={10}
  //                   value={basicDetails.mobile}
  //                   onChange={handleBasicDetailsChange}
  //                   showRequired={false}
  //                 />
  //                 <InputField
  //                   label="Email"
  //                   name="email"
  //                   value={basicDetails.email}
  //                   onChange={handleBasicDetailsChange}
  //                   showRequired={false}
  //                 />
  //               </div>
  //               <div className="mt-6 flex justify-end">
  //                 <button
  //                   type="button"
  //                   onClick={handleStep1Next}
  //                   disabled={isSendingOtp}
  //                   className="px-6 py-2 rounded-lg bg-[#004276] text-white hover:bg-[#003a5c] disabled:opacity-60"
  //                 >
  //                   {isSendingOtp ? "Sending OTP..." : "Next"}
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </DashboardLayout>
  //     </>
  //   );
  // }

  // Step 2 UI: verify OTP
  // if (currentStep === 2) {
  //   return (
  //     <>
  //       {isLoggingIn && <FullScreenLoader message="" />}

  //       <DashboardLayout>
  //         <div className="min-h-screen bg-white p-4 md:pt-10">
  //           <h2 className="text-lg font-semibold text-[#004276] md:text-xl">
  //             Doctor Onboard
  //           </h2>

  //           <div className="mt-8 space-y-8">
  //             <div className="rounded-lg border border-gray-200 p-6 shadow-sm max-w-3xl">
  //               <h3 className="mb-2 text-lg font-semibold text-[#004276]">
  //                 Step 2 of 3 - Verify OTP
  //               </h3>
  //               <p className="text-sm text-gray-600 mb-6">
  //                 OTP has been sent to{" "}
  //                 <span className="font-medium">{basicDetails.mobile}</span>. Enter the OTP below to continue.
  //               </p>
  //               <div className="max-w-sm">
  //                 <input
  //                   type="text"
  //                   maxLength={6}
  //                   value={otp}
  //                   onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
  //                   className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84]"
  //                   placeholder="Enter 6-digit OTP"
  //                 />
  //               </div>
  //               <div className="mt-6 flex justify-between max-w-sm">
  //                 <button
  //                   type="button"
  //                   onClick={() => setCurrentStep(1)}
  //                   className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
  //                 >
  //                   Back
  //                 </button>
  //                 <button
  //                   type="button"
  //                   onClick={handleVerifyOtp}
  //                   disabled={isVerifyingOtp}
  //                   className="px-6 py-2 rounded-lg bg-[#004276] text-white hover:bg-[#003a5c] disabled:opacity-60"
  //                 >
  //                   {isVerifyingOtp ? "Verifying..." : "Verify & Continue"}
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </DashboardLayout>
  //     </>
  //   );
  // }

  // Step 3 UI: existing full form
  return (
    <>
      {isLoggingIn && <FullScreenLoader message="" />}

      <DashboardLayout>
        <div className="min-h-screen bg-white p-4 md:pt-10">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-100 flex items-center justify-center">
              <UserPlus size={26} className="text-emerald-600" />
            </div>
            <h2 className="text-lg font-bold text-[#334155] md:text-xl tracking-tight">
              Doctor Onboard
            </h2>
          </div>

          <div className="mt-8 space-y-8">
            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* BASIC DOCTOR INFO */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <InputField
                  label="Doctor Name"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleChange}
                  error={fieldErrors.doctorName}
                  disabled={Boolean(doctorData?.doctorName)}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    State Medical Council{" "}
                    {mciVerificationStatus !== "NOT_APPLICABLE" && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative medical-council-dropdown-container">
                    <input
                      type="text"
                      placeholder={
                        mciVerificationStatus === "NOT_APPLICABLE"
                          ? "Not Applicable"
                          : "Search or select state medical council..."
                      }
                      value={medicalCouncilSearch}
                      onChange={(e) => {
                        setMedicalCouncilSearch(e.target.value);
                        setShowMedicalCouncilDropdown(true);
                      }}
                      onFocus={() => setShowMedicalCouncilDropdown(true)}
                      disabled={mciVerificationStatus === "NOT_APPLICABLE"}
                      className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 ${fieldErrors.stateMedicalCouncil ? 'border-red-500' : 'border-gray-200'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowMedicalCouncilDropdown(!showMedicalCouncilDropdown)
                      }
                      disabled={mciVerificationStatus === "NOT_APPLICABLE"}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:cursor-not-allowed disabled:hover:text-gray-400"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${showMedicalCouncilDropdown ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {loadingMedicalCouncils && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#3cba84]"></div>
                      </div>
                    )}

                    {/* Dropdown for state medical council options */}
                    {showMedicalCouncilDropdown &&
                      medicalCouncilOptions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {medicalCouncilOptions
                            .filter(
                              (option) =>
                                !medicalCouncilSearch ||
                                option.name
                                  .toLowerCase()
                                  .includes(medicalCouncilSearch.toLowerCase())
                            )
                            .map((option) => (
                              <button
                                key={option.smcId}
                                type="button"
                                onClick={() => handleMedicalCouncilSelect(option)}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-100 text-sm ${formData.smcId === option.smcId
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-gray-700"
                                  }`}
                              >
                                {option.name}
                              </button>
                            ))}
                        </div>
                      )}
                  </div>
                  {fieldErrors.stateMedicalCouncil && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.stateMedicalCouncil}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Medical License Number{" "}
                    {mciVerificationStatus !== "NOT_APPLICABLE" && <span className="text-red-500">*</span>}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      placeholder={mciVerificationStatus === "NOT_APPLICABLE" ? "Not Applicable" : "Enter here"}
                      disabled={mciVerificationStatus === "NOT_APPLICABLE"}
                      className={`flex-1 rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 ${fieldErrors.licenseNumber ? 'border-red-500' : 'border-gray-200'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyMci}
                      disabled={
                        isVerifyingMci ||
                        mciVerificationStatus === "VERIFIED" ||
                        mciVerificationStatus === "NOT_APPLICABLE"
                      }
                      className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#004276] hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {mciVerificationStatus === "VERIFIED"
                        ? "Verified"
                        : mciVerificationStatus === "NOT_APPLICABLE"
                        ? "Not Applicable"
                        : isVerifyingMci
                        ? "Verifying..."
                        : "Verify"}
                    </button>
                  </div>
                  {fieldErrors.licenseNumber && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.licenseNumber}</p>
                  )}
                </div>

                <InputField
                  label="Qualifications"
                  name="qualifications"
                  value={formData.qualifications}
                  onChange={handleChange}
                  error={fieldErrors.qualifications}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Specialization <span className="text-red-500">*</span>
                  </label>
                  <div className="relative specialization-dropdown-container">
                    <input
                      type="text"
                      placeholder="Search or select specializations..."
                      value={specializationSearch}
                      onChange={(e) => setSpecializationSearch(e.target.value)}
                      onFocus={() => setShowSpecializationDropdown(true)}
                      className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${fieldErrors.specialization ? 'border-red-500' : 'border-gray-200'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowSpecializationDropdown(
                          !showSpecializationDropdown
                        )
                      }
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${showSpecializationDropdown ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {loadingSpecializations && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#3cba84]"></div>
                      </div>
                    )}

                    {/* Dropdown for specialization options */}
                    {showSpecializationDropdown &&
                      specializationOptions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {specializationOptions
                            .filter(
                              (option) =>
                                !specializationSearch ||
                                option
                                  .toLowerCase()
                                  .includes(specializationSearch.toLowerCase())
                            ) // Filter by search term if provided
                            .map((option, index) => (
                              <button
                                key={option || index}
                                type="button"
                                onClick={() =>
                                  handleSpecializationSelect(option)
                                }
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-gray-700"
                              >
                                {option}
                              </button>
                            ))}
                        </div>
                      )}
                  </div>

                  {/* Selected specializations */}
                  {formData.specialization.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.specialization.map((spec, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-[#3cba84] text-white px-3 py-1 rounded-full text-xs"
                        >
                          {spec}
                          <button
                            type="button"
                            onClick={() => handleSpecializationRemove(spec)}
                            className="ml-1 hover:bg-[#2da06f] rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Error message for specialization */}
                  {fieldErrors.specialization && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.specialization}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Doctor Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="doctorType"
                    value={formData.doctorType}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${fieldErrors.doctorType ? 'border-red-500' : 'border-gray-200'
                      }`}
                  >
                    <option value="">Select doctor type</option>
                    <option value="H360">H360</option>
                    <option value="EMPANELLED">Empanel</option>
                    <option value="INVENTORY">Inventory</option>
                    <option value="FREELANCER">Freelancer</option>
                    <option value="TOTALCARE">Total Care</option>
                    <option value="TAKECARE">Take Care</option>
                  </select>
                  {fieldErrors.doctorType && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.doctorType}</p>
                  )}
                </div>
                {(formData.doctorType === "EMPANELLED" || formData.doctorType === "INVENTORY" || formData.doctorType === "FREELANCER" ) && (
                  <div className="flex flex-col">
                    <label className="text-sm text-[#004276] mb-1 font-medium">
                      Doctor Sub Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="doctorSubType"
                      value={formData.doctorSubType}
                      onChange={handleChange}
                      className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${fieldErrors.doctorSubType ? 'border-red-500' : 'border-gray-200'
                        }`}
                    >
                      <option value="">Select doctor sub type</option>
                      <option value="GP">GP</option>
                      <option value="SP">SP</option>
                    </select>
                    {fieldErrors.doctorSubType && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.doctorSubType}</p>
                    )}
                  </div>
                )}
                <InputField
                  label="Experience (Years)"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  error={fieldErrors.experience}
                />
                <InputField
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  error={fieldErrors.phone}
                  disabled={Boolean(doctorData?.phone)}
                />
                <InputField
                  label="Email Id"
                  name="email1"
                  type="email"
                  value={formData.email1}
                  onChange={handleChange}
                  error={fieldErrors.email1}
                  disabled={Boolean(doctorData?.email)}
                />

                <InputField
                  label="Age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition border-gray-200"
                  >
                    <option value="">Select gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Family Doctor <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="isFamilyDoctor"
                    value={formData.isFamilyDoctor}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${fieldErrors.isFamilyDoctor ? 'border-red-500' : 'border-gray-200'
                      }`}
                  >
                    <option value="">Select</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  {fieldErrors.isFamilyDoctor && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.isFamilyDoctor}</p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Language
                  </label>
                  <div className="relative language-dropdown-container">
                    <div
                      onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                      className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition cursor-pointer bg-white flex items-center justify-between"
                    >
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                        {formData.languageSelection.length > 0
                          ? `${formData.languageSelection.length} selected`
                          : "Select language"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isLanguageOpen ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {isLanguageOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {LANGUAGE_OPTIONS.map((language) => (
                          <div
                            key={language}
                            onClick={() => {
                              const alreadySelected =
                                formData.languageSelection.includes(language);
                              setFormData((prev) => ({
                                ...prev,
                                languageSelection: alreadySelected
                                  ? prev.languageSelection.filter((l) => l !== language)
                                  : [...prev.languageSelection, language],
                              }));
                            }}
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${formData.languageSelection.includes(language)
                              ? "bg-blue-50 border-r-4 border-blue-500"
                              : ""
                              }`}
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border-2 mr-3 flex items-center justify-center ${formData.languageSelection.includes(language)
                                  ? "bg-blue-500 border-blue-500"
                                  : "border-gray-300"
                                  }`}
                              >
                                {formData.languageSelection.includes(language) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={formData.languageSelection.includes(language) ? "text-blue-700 font-medium" : "text-gray-700"}>
                                {language}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {formData.languageSelection.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.languageSelection.map((language) => (
                        <span
                          key={language}
                          className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs"
                        >
                          {language}
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                languageSelection: prev.languageSelection.filter(
                                  (l) => l !== language
                                ),
                              }))
                            }
                            className="ml-1 hover:bg-blue-100 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>


                <InputField
                  label="Pin Code"
                  name="pinCode"
                  maxLength={6}
                  value={formData.pinCode}
                  onChange={(e) => {
                    handleChange(e);
                    const val = e.target.value.replace(/\D/g, "");
                    if (val.length === 6) {
                      fetchAddressByPincode(val);
                    }
                  }}
                />
                <InputField
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
                <InputField
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
                 <InputField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />

              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* REQUIRED DOCUMENTS */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Required Documents
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    PAN Card Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      name="panCardNumber"
                      value={formData.panCardNumber}
                      onChange={handleChange}
                      placeholder="Enter here"
                      className={`flex-1 rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${fieldErrors.panCardNumber ? 'border-red-500' : 'border-gray-200'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyPan}
                      disabled={isVerifyingPan || panVerified === "VERIFIED"}
                      className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#004276] hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {panVerified === "VERIFIED"
                        ? "Verified"
                        : isVerifyingPan
                        ? "Verifying..."
                        : "Verify"}
                    </button>
                  </div>
                  {fieldErrors.panCardNumber && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.panCardNumber}</p>
                  )}
                </div>

                <FileInputField
                  label="PAN Photo (Image/PDF)"
                  name="panPhoto"
                  file={formData.panPhoto}
                  onChange={handleChange}
                  error={fieldErrors.panPhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, panPhoto: null }))}
                  required
                />

                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Aadhaar Number
                  </label>
                  <input
                    type="text"
                    name="adhharCardNo"
                    value={formData.adhharCardNo}
                    onChange={handleChange}
                    placeholder="Enter here"
                    className={`rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${fieldErrors.adhharCardNo ? 'border-red-500' : 'border-gray-200'
                      }`}
                  />
                  {fieldErrors.adhharCardNo && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.adhharCardNo}</p>
                  )}
                </div>

                <FileInputField
                  label="Aadhaar Photo (Image/PDF)"
                  name="adharPhoto"
                  file={formData.adharPhoto}
                  onChange={handleChange}
                  error={fieldErrors.adharPhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, adharPhoto: null }))}
                />

                <FileInputField
                  label="Signature Photo"
                  name="signaturePhoto"
                  file={formData.signaturePhoto}
                  onChange={handleChange}
                  error={fieldErrors.signaturePhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, signaturePhoto: null }))}
                />

                <FileInputField
                  label="Stamp Photo"
                  name="stampPhoto"
                  file={formData.stampPhoto}
                  onChange={handleChange}
                  error={fieldErrors.stampPhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, stampPhoto: null }))}
                />

                <FileInputField
                  label="Profile Photo"
                  name="profilePhoto"
                  file={formData.profilePhoto}
                  onChange={handleChange}
                  error={fieldErrors.profilePhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, profilePhoto: null }))}
                />
                <FileInputField
                  label="Cancelled Cheque Photo"
                  name="cancelledChequePhoto"
                  file={formData.cancelledChequePhoto}
                  onChange={handleChange}
                  error={fieldErrors.cancelledChequePhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, cancelledChequePhoto: null }))}
                />
                <FileInputField
                  label="Medical Registration Certificate Photo"
                  name="medicalRegistrationCertificatePhoto"
                  file={formData.medicalRegistrationCertificatePhoto}
                  onChange={handleChange}
                  error={fieldErrors.medicalRegistrationCertificatePhoto}
                  onDelete={() => setFormData(prev => ({ ...prev, medicalRegistrationCertificatePhoto: null }))}
                />
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* BANK DETAILS */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Bank Details
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <InputField
                  label="Bank Name"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  error={fieldErrors.bankName}
                  showRequired={true}
                />
                <InputField
                  label="Branch Location"
                  name="bankLoc"
                  value={formData.bankLoc}
                  onChange={handleChange}
                  error={fieldErrors.bankLoc}
                  showRequired={true}
                />
                <InputField
                  label="Account Holder Name"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  error={fieldErrors.accountHolderName}
                  showRequired={true}
                />
                <InputField
                  label="IFSC Code"
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleChange}
                  error={fieldErrors.ifscCode}
                  showRequired={true}
                  placeholder="SBIN0001234"
                  maxLength={11}
                />
                <InputField
                  label="Account Number"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  error={fieldErrors.accountNumber}
                  showRequired={true}
                />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleVerifyBank}
                  disabled={isVerifyingBank || bankVerified === "VERIFIED"}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#004276] hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {bankVerified === "VERIFIED"
                    ? "Verified"
                    : isVerifyingBank
                    ? "Verifying..."
                    : "Verify Bank Details"}
                </button>
                {bankVerified === "NOT_VERIFIED" && (
                  <span className="text-sm text-red-600">Not verified</span>
                )}
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* CONSULTATION FEES */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Consultation Fees <span className="text-red-500">*</span>
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Consultation Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="consultationtype"
                    value={consultationFeeEntry.consultationtype}
                    onChange={(e) =>
                      setConsultationFeeEntry((prev) => ({
                        ...prev,
                        consultationtype: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
                  >
                    <option value="">Select</option>
                    <option value="V">Video</option>
                    <option value="I">In-Person</option>
                    <option value="C">Camp</option>
                    <option value="H">Home Visit</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Fee (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    name="consultationFee"
                    value={consultationFeeEntry.consultationFee}
                    onChange={(e) =>
                      setConsultationFeeEntry((prev) => ({
                        ...prev,
                        consultationFee: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
                    placeholder="Enter fee"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <button
                    type="button"
                    className="w-full bg-[#004276] hover:bg-[#003a5c] text-white py-2 px-6 rounded-lg transition"
                    onClick={() => {
                      if (
                        consultationFeeEntry.consultationtype &&
                        consultationFeeEntry.consultationFee &&
                        !consultationFees.some(
                          (f) =>
                            f.consultationtype ===
                            consultationFeeEntry.consultationtype
                        )
                      ) {
                        setConsultationFees((prev) => [
                          ...prev,
                          {
                            consultationtype:
                              consultationFeeEntry.consultationtype,
                            consultationFee: Number(
                              consultationFeeEntry.consultationFee
                            ),
                          },
                        ]);
                        setConsultationFeeEntry({
                          consultationtype: "",
                          consultationFee: "",
                        });
                        // Clear error when fee is added
                        setFieldErrors(prev => ({ ...prev, consultationFees: null }));
                      } else if (
                        consultationFees.some(
                          (f) =>
                            f.consultationtype ===
                            consultationFeeEntry.consultationtype
                        )
                      ) {
                        toast.error("Fee for this type already added.");
                      } else {
                        toast.error("Please select type and enter fee.");
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* List of added fees */}
              {consultationFees.length > 0 && (
                <div className="mt-4 space-y-2">
                  {consultationFees.map((fee, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-lg"
                    >
                      <span className="text-sm text-gray-700">
                        {fee.consultationtype === "V" ? "Video" : fee.consultationtype === "I" ? "In-Person" : fee.consultationtype === "C" ? "Camp" : fee.consultationtype === "H" ? "Home Visit" : ""}{" "}
                        Consultation: ₹{fee.consultationFee}
                      </span>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 font-bold ml-4 transition"
                        onClick={() => {
                          const updatedFees = consultationFees.filter((_, i) => i !== idx);
                          setConsultationFees(updatedFees);
                          // Add error if no fees left
                          if (updatedFees.length === 0) {
                            setFieldErrors(prev => ({ ...prev, consultationFees: "At least one consultation fee is required" }));
                          }
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Error message for consultation fees */}
              {fieldErrors.consultationFees && (
                <p className="text-red-500 text-xs mt-2">{fieldErrors.consultationFees}</p>
              )}

              {/* Commission / Payment period */}
              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <InputField
                  label="Commission Percentage"
                  name="commissionPercentage"
                  type="number"
                  value={formData.commissionPercentage}
                  onChange={handleChange}
                  showRequired={false}
                />
                <InputField
                  label="Payment Period"
                  name="paymentPeriod"
                  value={formData.paymentPeriod}
                  onChange={handleChange}
                  placeholder="e.g. 1 Month"
                  showRequired={false}
                />
              </div>

            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* DOCTOR AVAILABILITY */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Doctor Availability <span className="text-red-500">*</span>
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:items-end lg:grid-cols-4">
                <div className="flex flex-col">
                  <DatePickerField
                    label="From Date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    error={fieldErrors.fromDate}
                  />
                </div>
                <div className="flex flex-col">
                  <DatePickerField
                    label="To Date"
                    name="toDate"
                    value={formData.toDate}
                    onChange={handleChange}
                    error={fieldErrors.toDate}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Weeks
                  </label>
                  <div className="relative weeks-dropdown-container">
                    <div
                      onClick={() => setIsWeeksOpen(!isWeeksOpen)}
                      className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition cursor-pointer bg-white whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        currentSlot.weekNumbers.length > 0
                          ? `Selected: ${currentSlot.weekNumbers.join(", ")}`
                          : "Select Week"
                      }
                    >
                      {currentSlot.weekNumbers.length > 0
                        ? `Selected: ${currentSlot.weekNumbers.join(", ")}`
                        : "Select Week"
                      }
                    </div>

                    {isWeeksOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {[1, 2, 3, 4, 5].map((week) => (
                          <div
                            key={week}
                            onClick={() => {
                              const alreadySelected = currentSlot.weekNumbers.includes(week);
                              setCurrentSlot((prev) => ({
                                ...prev,
                                weekNumbers: alreadySelected
                                  ? prev.weekNumbers.filter((w) => w !== week)
                                  : [...prev.weekNumbers, week],
                              }));
                            }}
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${currentSlot.weekNumbers.includes(week)
                              ? "bg-blue-50 border-r-4 border-blue-500"
                              : ""
                              }`}
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border-2 mr-3 flex items-center justify-center ${currentSlot.weekNumbers.includes(week)
                                  ? "bg-blue-500 border-blue-500"
                                  : "border-gray-300"
                                  }`}
                              >
                                {currentSlot.weekNumbers.includes(week) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={currentSlot.weekNumbers.includes(week) ? "text-blue-700 font-medium" : "text-gray-700"}>
                                Week {week}
                              </span>
                            </div>
                            {currentSlot.weekNumbers.includes(week) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentSlot((prev) => ({
                                    ...prev,
                                    weekNumbers: prev.weekNumbers.filter((w) => w !== week),
                                  }));
                                }}
                                className="w-5 h-5 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>


                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Days
                  </label>
                  <div className="relative days-dropdown-container">
                    <div
                      onClick={() => setIsDaysOpen(!isDaysOpen)}
                      className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition cursor-pointer bg-white"
                    >
                      {currentSlot.dayOfWeek.length > 0
                        ? `Selected: ${currentSlot.dayOfWeek.join(", ")}`
                        : "Select Day"
                      }
                    </div>

                    {isDaysOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {[
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday",
                          "Sunday"
                        ].map((day) => (
                          <div
                            key={day}
                            onClick={() => {
                              const alreadySelected = currentSlot.dayOfWeek.includes(day);
                              setCurrentSlot((prev) => ({
                                ...prev,
                                dayOfWeek: alreadySelected
                                  ? prev.dayOfWeek.filter((d) => d !== day)
                                  : [...prev.dayOfWeek, day],
                              }));
                            }}
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${currentSlot.dayOfWeek.includes(day)
                              ? "bg-blue-50 border-r-4 border-blue-500"
                              : ""
                              }`}
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border-2 mr-3 flex items-center justify-center ${currentSlot.dayOfWeek.includes(day)
                                  ? "bg-blue-500 border-blue-500"
                                  : "border-gray-300"
                                  }`}
                              >
                                {currentSlot.dayOfWeek.includes(day) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={currentSlot.dayOfWeek.includes(day) ? "text-blue-700 font-medium" : "text-gray-700"}>
                                {day}
                              </span>
                            </div>
                            {currentSlot.dayOfWeek.includes(day) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentSlot((prev) => ({
                                    ...prev,
                                    dayOfWeek: prev.dayOfWeek.filter((d) => d !== day),
                                  }));
                                }}
                                className="w-5 h-5 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <TimePicker
                    value={
                      currentSlot.startTime
                        ? dayjs(currentSlot.startTime, "HH:mm")
                        : null
                    }
                    onChange={(time, timeString) =>
                      setCurrentSlot((prev) => ({
                        ...prev,
                        startTime: timeString,
                      }))
                    }
                    format="HH:mm"
                    className="w-full"
                    minuteStep={5}
                    use12Hours={false}
                    placeholder="Select time"
                    needConfirm={false}
                    showNow={false}
                    disabledTime={() => ({
                      disabledHours: () =>
                        Array.from({ length: 6 }, (_, h) => h),
                    })}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <TimePicker
                    value={
                      currentSlot.endTime
                        ? dayjs(currentSlot.endTime, "HH:mm")
                        : null
                    }
                    onChange={(time, timeString) =>
                      setCurrentSlot((prev) => ({
                        ...prev,
                        endTime: timeString,
                      }))
                    }
                    format="HH:mm"
                    className="w-full"
                    minuteStep={5}
                    use12Hours={false}
                    placeholder="Select time"
                    needConfirm={false}
                    showNow={false}
                    disabledTime={() => ({
                      disabledHours: () =>
                        Array.from({ length: 6 }, (_, h) => h),
                    })}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-[#004276] mb-1 font-medium">
                    Consultation Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative consultation-type-dropdown-container">
                    <div
                      onClick={() =>
                        setIsConsultationTypeOpen(!isConsultationTypeOpen)
                      }
                      className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition cursor-pointer bg-white whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        currentSlot.consultationType.length > 0
                          ? `Selected: ${currentSlot.consultationType
                            .map(getConsultationTypeLabel)
                            .join(", ")}`
                          : "Select Consultation Type"
                      }
                    >
                      {currentSlot.consultationType.length > 0
                        ? `Selected: ${currentSlot.consultationType
                          .map(getConsultationTypeLabel)
                          .join(", ")}`
                        : "Select Consultation Type"}
                    </div>

                    {isConsultationTypeOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {consultationTypeOptions.map((type) => (
                          <div
                            key={type.value}
                            onClick={() => {
                              const alreadySelected =
                                currentSlot.consultationType.includes(
                                  type.value
                                );
                              setCurrentSlot((prev) => ({
                                ...prev,
                                consultationType: alreadySelected
                                  ? prev.consultationType.filter(
                                    (item) => item !== type.value
                                  )
                                  : [...prev.consultationType, type.value],
                              }));
                            }}
                            className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${currentSlot.consultationType.includes(type.value)
                              ? "bg-blue-50 border-r-4 border-blue-500"
                              : ""
                              }`}
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-4 h-4 border-2 mr-3 flex items-center justify-center ${currentSlot.consultationType.includes(type.value)
                                  ? "bg-blue-500 border-blue-500"
                                  : "border-gray-300"
                                  }`}
                              >
                                {currentSlot.consultationType.includes(type.value) && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={currentSlot.consultationType.includes(type.value) ? "text-blue-700 font-medium" : "text-gray-700"}>
                                {type.label}
                              </span>
                            </div>
                            {currentSlot.consultationType.includes(type.value) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentSlot((prev) => ({
                                    ...prev,
                                    consultationType:
                                      prev.consultationType.filter(
                                        (item) => item !== type.value
                                      ),
                                  }));
                                }}
                                className="w-5 h-5 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-start md:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (
                      currentSlot.dayOfWeek.length > 0 &&
                      currentSlot.startTime &&
                      currentSlot.endTime &&
                      currentSlot.consultationType.length > 0
                    ) {
                      const newSlots = currentSlot.dayOfWeek.flatMap((day) =>
                        currentSlot.consultationType.map((type) => {
                          const newSlot = {
                            dayOfWeek: day,
                            startTime: currentSlot.startTime,
                            endTime: currentSlot.endTime,
                            consultationType: type,
                          };

                          if (currentSlot.weekNumbers.length > 0) {
                            newSlot.weekNumbers = currentSlot.weekNumbers;
                          }

                          return newSlot;
                        })
                      );

                      setFormData((prev) => ({
                        ...prev,
                        timeSlots: [...prev.timeSlots, ...newSlots],
                      }));

                      setFieldErrors(prev => ({ ...prev, timeSlots: null }));

                      setCurrentSlot({
                        dayOfWeek: [],
                        startTime: "",
                        endTime: "",
                        weekNumbers: [1, 2, 3, 4, 5],
                        consultationType: [],
                      });
                    } else {
                      toast.error(
                        "Please fill all time-slot fields (Week is optional)"
                      );
                    }
                  }}
                  className="w-full md:w-40 bg-[#004276] hover:bg-[#003a5c] text-white py-2 rounded-lg transition"
                >
                  Add
                </button>
              </div>


              {formData.timeSlots.length > 0 && (
                <div className="mt-4 space-y-2">
                  {formData.timeSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-gray-100 px-4 py-2 rounded-lg"
                    >
                      <div className="text-sm text-gray-700">
                        {slot.dayOfWeek} – {slot.startTime} to {slot.endTime}
                        {(Array.isArray(slot.consultationType)
                          ? slot.consultationType.length > 0
                          : !!slot.consultationType) && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({Array.isArray(slot.consultationType)
                              ? slot.consultationType
                                .map(getConsultationTypeLabel)
                                .join(", ")
                              : getConsultationTypeLabel(slot.consultationType)})
                          </span>
                        )}
                        {slot.weekNumbers?.length > 0 && (
                          <span className="ml-2 text-xs text-gray-500">
                            (Weeks: {slot.weekNumbers.join(", ")})
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = formData.timeSlots.filter(
                            (_, i) => i !== index
                          );
                          setFormData((prev) => ({
                            ...prev,
                            timeSlots: updated,
                          }));
                          // Add error if no time slots left
                          if (updated.length === 0) {
                            setFieldErrors(prev => ({ ...prev, timeSlots: "At least one time slot is required" }));
                          }
                        }}
                        className="text-red-500 hover:text-red-700 font-bold transition"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}


              {fieldErrors.timeSlots && (
                <p className="text-red-500 text-xs mt-2">{fieldErrors.timeSlots}</p>
              )}



              {/* <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-[#004276] mt-4">
                Slot Duration
              </h3>
                <InputField
                  label="Duration (minutes)"
                  name="slotDuration"
                  value={formData.slotDuration}
                  onChange={handleChange}
                  error={fieldErrors.slotDuration}
                  type="number"
                  min="1"
                  placeholder="Enter duration in minutes"
                />

               
              </div> */}
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* SLOT DURATION */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                Slot Duration
              </h3>
              <div className="flex flex-col">

                <InputField
                  label="Duration (minutes)"
                  name="slotDuration"
                  value={formData.slotDuration}
                  onChange={handleChange}
                  error={fieldErrors.slotDuration}
                  type="number"
                  min="1"
                  placeholder="Enter duration in minutes"
                />


              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────────── */}
            {/* ABOUT DOCTOR */}
            {/* ────────────────────────────────────────────────────────────────────── */}
            <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#004276]">
                About Doctor
              </h3>
              <InputField
                label="Doctor Bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                type="textarea"
                rows={4}
                error={fieldErrors.bio}
              />
            </div>
          </div>

          {/* ────────────────────────────────────────────────────────────────────── */}
          {/* SUBMIT BUTTON */}
          {/* ────────────────────────────────────────────────────────────────────── */}
          <div className="flex justify-center mt-10">
            <button
              onClick={handleSubmitClick}
              // disabled={!isFormValid() || isLoggingIn}
              disabled={isLoggingIn || isVerifyingPan}
              className="w-full max-w-md rounded-full bg-gradient-to-r from-[#004172] to-[#3cba84] px-10 py-3 font-medium text-white disabled:opacity-50 transition-opacity md:w-[335px] md:max-w-none"
            >
              {isVerifyingPan ? "Verifying PAN..." : isLoggingIn ? "Loading..." : "Submit"}
            </button>
          </div>
        </div>
      </DashboardLayout>

      {panVerifyModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !isManualVerifyingPan && setPanVerifyModal({ isOpen: false, message: "", status: "" })}
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-semibold text-red-600">PAN Verification Failed</h3>
            <p className="text-sm text-gray-700">{panVerifyModal.message}</p>
            <div className="mt-4 flex justify-end gap-3">
              {panVerifyModal.status === "NOT_VERIFIED" ? (
                <>
                  <button
                    type="button"
                    onClick={() => setPanVerifyModal({ isOpen: false, message: "", status: "" })}
                    disabled={isManualVerifyingPan}
                    className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => handleManualPanVerification(true)}
                    disabled={isManualVerifyingPan}
                    className="px-4 py-2 rounded-lg bg-[#004276] text-white text-sm font-medium hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isManualVerifyingPan ? "Verifying..." : "Verify"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setPanVerifyModal({ isOpen: false, message: "", status: "" })}
                  className="px-4 py-2 rounded-lg bg-[#004276] text-white text-sm font-medium hover:bg-[#003a5c]"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {bankVerifyModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !isManualVerifyingBank && setBankVerifyModal({ isOpen: false, message: "", status: "" })}
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-semibold text-red-600">Bank Verification Failed</h3>
            <p className="text-sm text-gray-700">{bankVerifyModal.message}</p>
            <div className="mt-4 flex justify-end gap-3">
              {bankVerifyModal.status === "NOT_VERIFIED" ? (
                <>
                  <button
                    type="button"
                    onClick={() => setBankVerifyModal({ isOpen: false, message: "", status: "" })}
                    disabled={isManualVerifyingBank}
                    className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => handleManualBankVerification(true)}
                    disabled={isManualVerifyingBank}
                    className="px-4 py-2 rounded-lg bg-[#004276] text-white text-sm font-medium hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isManualVerifyingBank ? "Verifying..." : "Verify"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setBankVerifyModal({ isOpen: false, message: "", status: "" })}
                  className="px-4 py-2 rounded-lg bg-[#004276] text-white text-sm font-medium hover:bg-[#003a5c]"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {mciVerifyModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !isManualVerifyingMci && setMciVerifyModal({ isOpen: false, message: "", result: null, needsManualVerification: false })}
        >
          <div
            className={`relative w-full rounded-lg bg-white p-5 shadow-lg ${mciVerifyModal.result ? "max-w-lg" : "max-w-md"
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className={`mb-2 text-lg font-semibold ${mciVerificationStatus === "VERIFIED" ? "text-emerald-600" : "text-red-600"
                }`}
            >
              {mciVerificationStatus === "VERIFIED" ? "Medical License Verified" : "Medical License Verification Failed"}
            </h3>
            <p className="text-sm text-gray-700">{mciVerifyModal.message}</p>
            {mciVerifyModal.needsManualVerification && (
              <p className="mt-2 text-sm text-gray-600">You can verify manually.</p>
            )}

            {mciVerifyModal.result && (
              <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(mciVerifyModal.result)
                      .filter(
                        ([key, value]) =>
                          value !== null &&
                          value !== undefined &&
                          value !== "" &&
                          !MCI_RESULT_HIDDEN_FIELDS.includes(key)
                      )
                      .map(([key, value]) => (
                        <tr key={key} className="border-b border-gray-100 last:border-b-0">
                          <td className="px-3 py-2 font-medium text-[#004276] whitespace-nowrap align-top">
                            {MCI_RESULT_LABELS[key] || key}
                          </td>
                          <td className="px-3 py-2 text-gray-700 break-words">
                            {String(value)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end gap-3">
              {mciVerifyModal.needsManualVerification ? (
                <>
                  <button
                    type="button"
                    onClick={() => setMciVerifyModal({ isOpen: false, message: "", result: null, needsManualVerification: false })}
                    disabled={isManualVerifyingMci}
                    className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleManualMciVerification}
                    disabled={isManualVerifyingMci}
                    className="px-4 py-2 rounded-lg bg-[#004276] text-white text-sm font-medium hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isManualVerifyingMci ? "Verifying..." : "Verify"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setMciVerifyModal({ isOpen: false, message: "", result: null, needsManualVerification: false })}
                  className="px-4 py-2 rounded-lg bg-[#004276] text-white text-sm font-medium hover:bg-[#003a5c]"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  rows = 1,
  showRequired = true,
  error,
  ...rest
}) {
  const isTextarea = type === "textarea";
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} {showRequired && <span className="text-red-500">*</span>}
      </label>
      {isTextarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={rows}
          placeholder="Enter here"
          required={showRequired}
          className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition resize-none ${error ? 'border-red-500' : 'border-gray-200'
            }`}
          {...rest}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder="Enter here"
          required={showRequired}
          className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${error ? 'border-red-500' : 'border-gray-200'
            }`}
          {...rest}
        />
      )}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function FileInputField({ label, name, file, onChange, error, onDelete, required }) {
  const [previewURL, setPreviewURL] = useState(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewURL(url);
      return () => {
        URL.revokeObjectURL(url);
        setPreviewURL(null);
      };
    } else {
      setPreviewURL(null);
    }
  }, [file]);

  const isImage = file && file.type.startsWith("image/");

  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="file"
        name={name}
        accept="image/*,application/pdf"
        onChange={onChange}
        className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${error ? 'border-red-500' : 'border-gray-200'
          }`}
      />

      {file && (
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-600">Selected: {file.name}</p>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="text-red-500 hover:text-red-700 ml-2 p-1 rounded-full hover:bg-red-50 transition"
              title="Delete file"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {isImage && previewURL && (
        <img
          src={previewURL}
          alt="Preview"
          className="mt-2 max-w-[200px] max-h-[150px] object-contain border border-gray-200 rounded-md shadow-sm"
        />
      )}

      {/* Only render PDF link if file exists AND is a PDF */}
      {file && !isImage && previewURL && file.type === "application/pdf" && (
        <p className="mt-2">
          <a
            href={previewURL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#004276] underline text-sm hover:text-[#003a5c] transition"
          >
            View PDF
          </a>
        </p>
      )}

      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function DatePickerField({ label, name, value, onChange, error }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} <span className="text-red-500">*</span>
      </label>
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={(date) => {
          onChange({
            target: {
              name,
              value: date ? date.format("YYYY-MM-DD") : "",
            },
          });
        }}
        format="DD-MM-YYYY"
        placeholder="DD-MM-YYYY"
        className={`w-full border border-gray-200 rounded-lg px-3 py-2 ${error ? 'border-red-500' : 'border-gray-200'
          }`}
        showToday
        allowClear
      />
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
}

function SelectField({ label, name, value, onChange, options }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} <span className="text-red-500">*</span>
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
      >
        <option value="">Select</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

export default CurebayDoctorOnboarding;